import { memo, useEffect, useRef, type CSSProperties } from 'react';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH, InputAction } from '../../game/constants';
import type { InputManager } from '../../game/input/InputManager';
import { clamp } from '../../game/math/scalar';
import type { GameWorld } from '../../game/world/GameWorld';

interface Props {
  input: InputManager | null;
  world: GameWorld | null;
  opacity: number;
  visible: boolean;
}

interface DirectActionBinding {
  element: HTMLButtonElement;
  action: InputAction;
  start: (event: TouchEvent) => void;
  end: (event: TouchEvent) => void;
}

/**
 * Sheet 01/03/05 draw an explicit TOUCH/PRESS state, and `:active` cannot carry
 * it: every handler below calls preventDefault(), which suppresses the active
 * flag on touch in several engines. So the state is a class, toggled alongside
 * the existing input calls — never in place of them, and never before their
 * guards, or a rejected second finger would light the button anyway.
 */
const DOWN_CLASS = 'is-down';

/**
 * Drag movement rides InputManager's pointer-follow trio
 * (setPointerTarget / setPointerFire / pulsePointerAction), NOT the touch-
 * labelled twins (setTouchFire / pulseTouchAction). Those call
 * noteDirectTouch(), which arms `suppressMouseUntil` for 850ms to shield the
 * pointer path from ghost mouse events after a real touch — but that same gate
 * sits in front of setPointerTarget too (via notePointer()), so calling any
 * *Touch* method from this layer would silently freeze the drag target for
 * the next 850ms after every autofire edge or FORM/DIR/DEVICE tap. Staying on
 * the pointer-side trio (which never calls noteDirectTouch()) sidesteps that
 * without touching InputManager. The only externally-visible cost is
 * `lastInputKind` reading 'pointer' instead of 'touch' — cosmetic only (the
 * bottom-rail readout it feeds is hidden on mobile play anyway).
 */
const TOUCH_MOVE_SENSITIVITY = 1.15;

/** Fix 3(b): input/world are stable GameRuntime references, opacity/visible are
 *  primitives — memo skips this whenever an unrelated App re-render leaves all
 *  four unchanged. */
export const TouchControls = memo(function TouchControls({ input, world, opacity, visible }: Props) {
  const captureRef = useRef<HTMLDivElement>(null);
  const fireRef = useRef<HTMLButtonElement>(null);
  const formationRef = useRef<HTMLButtonElement>(null);
  const directionRef = useRef<HTMLButtonElement>(null);
  const deviceRef = useRef<HTMLButtonElement>(null);
  const moveTouchIdRef = useRef<number | null>(null);
  const originScreenXRef = useRef(0);
  const originScreenYRef = useRef(0);
  const originSimXRef = useRef(0);
  const originSimYRef = useRef(0);

  useEffect(() => {
    const capture = captureRef.current;
    const fire = fireRef.current;
    const formation = formationRef.current;
    const direction = directionRef.current;
    const device = deviceRef.current;
    if (!visible || !capture || !fire || !formation || !direction || !device || !input || !world) return;
    const fireTouchIds = new Set<number>();
    let captureWidth = capture.clientWidth;
    let captureHeight = capture.clientHeight;
    let pendingMoveX = 0;
    let pendingMoveY = 0;
    let moveFrame = 0;
    const resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;
      captureWidth = entry.contentRect.width;
      captureHeight = entry.contentRect.height;
    });
    resizeObserver.observe(capture);

    const applyMoveTarget = (clientX: number, clientY: number): void => {
      if (captureWidth <= 0 || captureHeight <= 0) return;
      const screenDeltaX = clientX - originScreenXRef.current;
      const screenDeltaY = clientY - originScreenYRef.current;
      // The capture layer fills the phone so portrait can use the lower touch
      // deck. Map gesture distance against the largest contained 16:9 field,
      // not the tall portrait capture rect, or vertical movement would require
      // dragging almost the entire screen.
      const referenceWidth = Math.min(captureWidth, captureHeight * (INTERNAL_WIDTH / INTERNAL_HEIGHT));
      const referenceHeight = Math.min(captureHeight, captureWidth * (INTERNAL_HEIGHT / INTERNAL_WIDTH));
      const simDeltaX = screenDeltaX * (INTERNAL_WIDTH / referenceWidth) * TOUCH_MOVE_SENSITIVITY;
      const simDeltaY = screenDeltaY * (INTERNAL_HEIGHT / referenceHeight) * TOUCH_MOVE_SENSITIVITY;
      const targetX = clamp(originSimXRef.current + simDeltaX, 0, INTERNAL_WIDTH);
      const targetY = clamp(originSimYRef.current + simDeltaY, 0, INTERNAL_HEIGHT);
      input.setPointerTarget(targetX, targetY);
    };

    const onCaptureStart = (event: TouchEvent): void => {
      if (event.changedTouches.length === 0) return;
      event.preventDefault();
      for (let i = 0; i < event.changedTouches.length; i += 1) {
        const touch = event.changedTouches[i];
        if (!touch) continue;
        if (moveTouchIdRef.current !== null) continue;
        moveTouchIdRef.current = touch.identifier;
        originScreenXRef.current = touch.clientX;
        originScreenYRef.current = touch.clientY;
        originSimXRef.current = world.player.x;
        originSimYRef.current = world.player.y;
      }
    };

    const onCaptureMove = (event: TouchEvent): void => {
      const id = moveTouchIdRef.current;
      if (id === null) return;
      for (let i = 0; i < event.changedTouches.length; i += 1) {
        const touch = event.changedTouches[i];
        if (!touch || touch.identifier !== id) continue;
        pendingMoveX = touch.clientX;
        pendingMoveY = touch.clientY;
        if (moveFrame === 0) {
          moveFrame = requestAnimationFrame(() => {
            moveFrame = 0;
            applyMoveTarget(pendingMoveX, pendingMoveY);
          });
        }
        return;
      }
    };

    const onCaptureEnd = (event: TouchEvent): void => {
      if (event.changedTouches.length === 0) return;
      event.preventDefault();
      for (let i = 0; i < event.changedTouches.length; i += 1) {
        const touch = event.changedTouches[i];
        if (!touch) continue;
        // Losing the movement finger keeps the last target (no snap-back);
        // a still-down second finger simply stops being able to steer.
        if (moveTouchIdRef.current === touch.identifier) moveTouchIdRef.current = null;
      }
    };

    const onFireStart = (event: TouchEvent): void => {
      event.preventDefault();
      for (let i = 0; i < event.changedTouches.length; i += 1) {
        const touch = event.changedTouches[i];
        if (touch) fireTouchIds.add(touch.identifier);
      }
      input.setPointerFire(true);
      fire.classList.add(DOWN_CLASS);
    };

    const onFireEnd = (event: TouchEvent): void => {
      event.preventDefault();
      for (let i = 0; i < event.changedTouches.length; i += 1) {
        const touch = event.changedTouches[i];
        if (touch) fireTouchIds.delete(touch.identifier);
      }
      input.setPointerFire(fireTouchIds.size > 0);
      if (fireTouchIds.size === 0) fire.classList.remove(DOWN_CLASS);
    };

    const actions: DirectActionBinding[] = [
      { element: formation, action: InputAction.Formation, start: () => undefined, end: () => undefined },
      { element: direction, action: InputAction.NodeDirectionRight, start: () => undefined, end: () => undefined },
      { element: device, action: InputAction.Device, start: () => undefined, end: () => undefined },
    ];
    for (let i = 0; i < actions.length; i += 1) {
      const binding = actions[i]!;
      const start = (event: TouchEvent): void => {
        event.preventDefault();
        input.pulsePointerAction(binding.action);
        binding.element.classList.add(DOWN_CLASS);
      };
      const end = (event: TouchEvent): void => {
        event.preventDefault();
        binding.element.classList.remove(DOWN_CLASS);
      };
      binding.start = start;
      binding.end = end;
      binding.element.addEventListener('touchstart', start, { passive: false });
      binding.element.addEventListener('touchend', end, { passive: false });
      binding.element.addEventListener('touchcancel', end, { passive: false });
    }

    capture.addEventListener('touchstart', onCaptureStart, { passive: false });
    // touchstart already cancels browser gestures and CSS declares
    // touch-action:none. Keeping the high-frequency move listener passive
    // removes Safari's synchronous scroll-blocking round trip.
    capture.addEventListener('touchmove', onCaptureMove, { passive: true });
    capture.addEventListener('touchend', onCaptureEnd, { passive: false });
    capture.addEventListener('touchcancel', onCaptureEnd, { passive: false });
    fire.addEventListener('touchstart', onFireStart, { passive: false });
    fire.addEventListener('touchend', onFireEnd, { passive: false });
    fire.addEventListener('touchcancel', onFireEnd, { passive: false });
    return () => {
      resizeObserver.disconnect();
      if (moveFrame !== 0) cancelAnimationFrame(moveFrame);
      capture.removeEventListener('touchstart', onCaptureStart);
      capture.removeEventListener('touchmove', onCaptureMove);
      capture.removeEventListener('touchend', onCaptureEnd);
      capture.removeEventListener('touchcancel', onCaptureEnd);
      fire.removeEventListener('touchstart', onFireStart);
      fire.removeEventListener('touchend', onFireEnd);
      fire.removeEventListener('touchcancel', onFireEnd);
      fire.classList.remove(DOWN_CLASS);
      for (let i = 0; i < actions.length; i += 1) {
        const binding = actions[i]!;
        binding.element.removeEventListener('touchstart', binding.start);
        binding.element.removeEventListener('touchend', binding.end);
        binding.element.removeEventListener('touchcancel', binding.end);
        binding.element.classList.remove(DOWN_CLASS);
      }
      moveTouchIdRef.current = null;
      fireTouchIds.clear();
      input.setPointerFire(false);
    };
  }, [input, world, visible]);

  if (!visible) return null;
  const style = { '--touch-opacity': String(opacity) } as CSSProperties;
  return (
    <div className="touch-controls" style={style} aria-label="Touch game controls">
      {/* Full-surface drag capture: first finger = movement (relative drag from
          its own current position). Sits below the FIRE/FORM/DIR/DEVICE buttons
          in DOM order so their own touch handlers win
          hit-testing over this layer. Touch-only by construction (native touch
          events) — desktop mouse keeps its separate GameRuntime pointer path. */}
      <div ref={captureRef} className="touch-capture" aria-hidden="true" />
      <div className="touch-guide" aria-hidden="true">
        <strong>DRAG TO MOVE</strong>
        <span>RIGHT · HOLD FIRE</span>
      </div>
      <button ref={fireRef} className="touch-fire" type="button" aria-label="Fire" />
      <div className="touch-actions">
        <button ref={formationRef} className="touch-fn" type="button">FORM</button>
        <button ref={directionRef} className="touch-fn" type="button">DIR</button>
        <button ref={deviceRef} className="touch-fn" type="button">DEVICE</button>
      </div>
    </div>
  );
});
